import { FC, useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import QRCode from "qrcode";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import type { BadgeTemplate, BadgeElement, QrCode, Page } from "../types";
import { loadTemplates, parseTemplateData } from "./BadgeTemplatesPage";
import api from "../services/api";

interface Assignment {
    selected: boolean;
    area: string;
    location: string;
}

const AREA_OPTIONS = [
    'ALL AREAS', 'STAGE', 'CATERING', 'PARCHEGGI', 'TOUR', 'PRESS', 'VIP', 'STAFF', 'HOSPITALITY',
];

const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
    const words = text.split(' ');
    const lines: string[] = [];
    let current = '';
    for (const word of words) {
        const test = current ? `${current} ${word}` : word;
        if (ctx.measureText(test).width <= maxWidth) { current = test; }
        else { if (current) lines.push(current); current = word; }
    }
    if (current) lines.push(current);
    return lines;
};

const roundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
    r = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y); ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r); ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h); ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r); ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
};

const generateBadgePng = async (
    template: BadgeTemplate,
    side: 'front' | 'back',
    name: string,
    qrValue: string,
    badgeNumber: number,
    area: string,
    location: string
): Promise<string> => {
    const W = template.width;
    const H = template.height;
    const sideData = template[side];

    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d')!;

    // Canvas background color
    ctx.fillStyle = sideData.bgColor || '#1e293b';
    ctx.fillRect(0, 0, W, H);

    for (const el of sideData.elements) {

        if (el.type === 'image') {
            if (!el.imageData) continue;
            const img = new Image();
            img.src = el.imageData;
            await new Promise<void>(resolve => { img.onload = () => resolve(); img.onerror = () => resolve(); });
            ctx.drawImage(img, el.x, el.y, el.width, el.height);
            continue;
        }

        if (el.type === 'box') {
            ctx.save();
            roundedRect(ctx, el.x, el.y, el.width, el.height, el.borderRadius || 0);
            if (el.bgColor && el.bgColor !== 'transparent') { ctx.fillStyle = el.bgColor; ctx.fill(); }
            if ((el.borderWidth || 0) > 0 && el.borderColor) { ctx.strokeStyle = el.borderColor; ctx.lineWidth = el.borderWidth!; ctx.stroke(); }
            ctx.restore();
            continue;
        }

        if (el.type === 'qrcode') {
            if (!qrValue) continue;
            try {
                const qrDataUrl = await QRCode.toDataURL(qrValue, { width: el.width, margin: 1, color: { dark: '#000000', light: '#ffffff' } });
                const qrImg = new Image();
                qrImg.src = qrDataUrl;
                await new Promise<void>(resolve => { qrImg.onload = () => resolve(); });
                ctx.drawImage(qrImg, el.x, el.y, el.width, el.height);
            } catch (_) {}
            continue;
        }

        const textValue =
            el.type === 'name'     ? name :
            el.type === 'area'     ? area :
            el.type === 'location' ? location :
            el.type === 'number'   ? String(badgeNumber) :
            (el.text || '');

        if (!textValue) continue;

        ctx.fillStyle = el.color || '#ffffff';
        const fw = el.fontStyle.includes('bold') ? 'bold' : 'normal';
        const fi = el.fontStyle.includes('italic') ? 'italic' : 'normal';
        ctx.font = `${fi} ${fw} ${el.fontSize}px Arial, sans-serif`;
        ctx.textAlign = el.align as CanvasTextAlign;
        ctx.textBaseline = 'top';

        const textX =
            el.align === 'center' ? el.x + el.width / 2 :
            el.align === 'right'  ? el.x + el.width : el.x;

        const lines = wrapText(ctx, textValue, el.width);
        const lineH = el.fontSize * 1.2;
        lines.forEach((line, i) => ctx.fillText(line, textX, el.y + i * lineH));
    }

    return canvas.toDataURL('image/png');
};

const BadgeBatchPage: FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const base = location.pathname.startsWith('/admin') ? '/admin' : '/client';

    const [template, setTemplate] = useState<BadgeTemplate | null>(null);
    const [qrCodes, setQrCodes] = useState<QrCode[]>([]);
    const [assignments, setAssignments] = useState<Record<number, Assignment>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [selectAll, setSelectAll] = useState(false);
    const [defaultArea, setDefaultArea] = useState('ALL AREAS');
    const [defaultLocation, setDefaultLocation] = useState('');

    useEffect(() => {
        loadTemplates().then(templates => {
            const tpl = templates.find(t => t.id === Number(id));
            if (!tpl) { navigate(`${base}/badges`); return; }
            setTemplate(tpl);
        });

        api.get('/api/qrcodes/getAll', {
            params: { page: 0, size: 500, idCategory: -1, search: '', onlyActive: false },
        }).then((res: any) => {
            const codes: QrCode[] = res.data?.data?.content || [];
            setQrCodes(codes);
            const init: Record<number, Assignment> = {};
            codes.forEach(qr => { init[qr.id] = { selected: false, area: 'ALL AREAS', location: '' }; });
            setAssignments(init);
        }).catch((err: any) => {
            const msg = err?.response?.data?.message || err?.message || 'Errore di rete';
            setError(`Errore nel caricamento dei QR code: ${msg}`);
        }).finally(() => setIsLoading(false));
    }, [id, navigate, base]);

    const updateAssignment = (qrId: number, patch: Partial<Assignment>) => {
        setAssignments(prev => ({ ...prev, [qrId]: { ...prev[qrId], ...patch } }));
    };

    const handleSelectAll = (checked: boolean) => {
        setSelectAll(checked);
        setAssignments(prev => {
            const next = { ...prev };
            filteredQrs.forEach(qr => { next[qr.id] = { ...next[qr.id], selected: checked }; });
            return next;
        });
    };

    const applyDefaults = () => {
        setAssignments(prev => {
            const next = { ...prev };
            filteredQrs.filter(qr => prev[qr.id]?.selected).forEach(qr => {
                next[qr.id] = { ...next[qr.id], area: defaultArea, location: defaultLocation };
            });
            return next;
        });
    };

    const filteredQrs = qrCodes.filter(qr => qr.name.toLowerCase().includes(search.toLowerCase()));
    const selectedQrs = qrCodes.filter(qr => assignments[qr.id]?.selected);

    const handleGenerate = async () => {
        if (!template || selectedQrs.length === 0) return;
        setGenerating(true);
        setProgress({ current: 0, total: selectedQrs.length });
        setError(null);

        try {
            const zip = new JSZip();

            // Group by area for folder structure
            const byArea: Record<string, QrCode[]> = {};
            for (const qr of selectedQrs) {
                const area = (assignments[qr.id]?.area || 'GENERALE').replace(/[/\\?%*:|"<>]/g, '_');
                if (!byArea[area]) byArea[area] = [];
                byArea[area].push(qr);
            }

            let badgeNumber = 1;
            let done = 0;

            for (const [area, qrs] of Object.entries(byArea)) {
                const folder = zip.folder(area) ?? zip;
                for (const qr of qrs) {
                    const location = assignments[qr.id]?.location || '';
                    const safeName = qr.name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');

                    const [frontUrl, backUrl] = await Promise.all([
                        generateBadgePng(template, 'front', qr.name, qr.code, badgeNumber, area, location),
                        generateBadgePng(template, 'back', qr.name, qr.code, badgeNumber, area, location),
                    ]);

                    folder.file(`${String(badgeNumber).padStart(3, '0')}_${safeName}_fronte.png`, frontUrl.split(',')[1], { base64: true });
                    folder.file(`${String(badgeNumber).padStart(3, '0')}_${safeName}_retro.png`, backUrl.split(',')[1], { base64: true });

                    badgeNumber++;
                    done++;
                    setProgress({ current: done, total: selectedQrs.length });
                }
            }

            const blob = await zip.generateAsync({ type: 'blob' });
            const tplName = template.name.replace(/\s+/g, '_');
            const date = new Date().toISOString().split('T')[0];
            saveAs(blob, `badge_${tplName}_${date}.zip`);
        } catch (err: any) {
            setError(`Errore durante la generazione: ${err.message}`);
        } finally {
            setGenerating(false);
            setProgress(null);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500"/>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <button onClick={() => navigate(`${base}/badges`)} className="text-slate-500 hover:text-white transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
                        </button>
                        <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
                            Genera Badge
                        </h1>
                    </div>
                    <p className="text-slate-400 text-sm">Template: <span className="text-amber-400 font-medium">{template?.name}</span> · {selectedQrs.length} selezionati</p>
                </div>
                <button
                    onClick={handleGenerate}
                    disabled={generating || selectedQrs.length === 0}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-amber-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                    {generating ? (
                        <><div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full"/> Generando... {progress && `(${progress.current}/${progress.total})`}</>
                    ) : (
                        <><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Genera & Scarica ZIP ({selectedQrs.length})</>
                    )}
                </button>
            </div>

            {error && (
                <div className="p-4 bg-red-900/20 border border-red-800/50 text-red-300 rounded-xl text-sm">{error}</div>
            )}

            {/* Progress bar */}
            {generating && progress && (
                <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
                    <div className="flex justify-between text-sm text-slate-400 mb-2">
                        <span>Generazione in corso...</span>
                        <span>{progress.current} / {progress.total}</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                        <div
                            className="bg-gradient-to-r from-amber-500 to-orange-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(progress.current / progress.total) * 100}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Default values + search */}
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-4 space-y-3">
                <p className="text-sm font-semibold text-slate-300">Impostazioni rapide</p>
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                        <label className="text-xs text-slate-500 mb-1 block">Area predefinita per selezione</label>
                        <select
                            value={defaultArea}
                            onChange={e => setDefaultArea(e.target.value)}
                            className="w-full bg-slate-900/50 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500"
                        >
                            {AREA_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>
                    <div className="flex-1">
                        <label className="text-xs text-slate-500 mb-1 block">Location predefinita</label>
                        <input
                            type="text"
                            value={defaultLocation}
                            onChange={e => setDefaultLocation(e.target.value)}
                            placeholder="es. ROMA"
                            className="w-full bg-slate-900/50 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500"
                        />
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={applyDefaults}
                            disabled={selectedQrs.length === 0}
                            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                            Applica ai selezionati
                        </button>
                    </div>
                </div>
            </div>

            {/* QR Code table */}
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-slate-700/50 flex flex-col sm:flex-row gap-3 justify-between items-center">
                    <h2 className="text-base font-bold text-white">Seleziona QR Code ({qrCodes.length} totali)</h2>
                    <div className="relative w-full sm:w-64">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                        <input
                            type="text"
                            placeholder="Cerca per nome..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full bg-slate-900/50 border border-slate-700 text-slate-200 text-sm rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:border-amber-500"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-900/50 text-xs text-slate-400 uppercase tracking-wider">
                            <tr>
                                <th className="p-3 w-10">
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={e => handleSelectAll(e.target.checked)}
                                        className="rounded text-amber-500 focus:ring-amber-500 bg-slate-800 border-slate-600 cursor-pointer"
                                    />
                                </th>
                                <th className="p-3 text-left">Nome QR Code</th>
                                <th className="p-3 text-left">Area Accesso</th>
                                <th className="p-3 text-left">Location / Città</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/40">
                            {filteredQrs.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="p-8 text-center text-slate-500">Nessun QR code trovato.</td>
                                </tr>
                            ) : filteredQrs.map(qr => {
                                const asgn = assignments[qr.id] ?? { selected: false, area: 'ALL AREAS', location: '' };
                                return (
                                    <tr key={qr.id} className={`transition-colors ${asgn.selected ? 'bg-amber-500/5' : 'hover:bg-slate-700/20'}`}>
                                        <td className="p-3">
                                            <input
                                                type="checkbox"
                                                checked={asgn.selected}
                                                onChange={e => updateAssignment(qr.id, { selected: e.target.checked })}
                                                className="rounded text-amber-500 focus:ring-amber-500 bg-slate-800 border-slate-600 cursor-pointer"
                                            />
                                        </td>
                                        <td className="p-3">
                                            <span className={`font-medium ${asgn.selected ? 'text-white' : 'text-slate-400'}`}>{qr.name}</span>
                                            <span className="text-slate-600 text-xs ml-2 font-mono">{qr.code?.slice(0, 12)}…</span>
                                        </td>
                                        <td className="p-3">
                                            <select
                                                value={asgn.area}
                                                onChange={e => updateAssignment(qr.id, { area: e.target.value })}
                                                disabled={!asgn.selected}
                                                className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-500 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                {AREA_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                                            </select>
                                        </td>
                                        <td className="p-3">
                                            <input
                                                type="text"
                                                value={asgn.location}
                                                onChange={e => updateAssignment(qr.id, { location: e.target.value })}
                                                disabled={!asgn.selected}
                                                placeholder="ROMA"
                                                className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-lg px-2 py-1.5 w-28 focus:outline-none focus:border-amber-500 disabled:opacity-40 disabled:cursor-not-allowed"
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Template preview info */}
            {template && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {(['front', 'back'] as const).map(side => (
                        <div key={side} className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-3 flex items-center gap-4">
                            {(() => { const img = template[side].elements.find(e => e.type === 'image')?.imageData; return img
                                ? <img src={img} alt={side} className="h-20 w-auto object-contain rounded shadow"/>
                                : <div className="h-20 aspect-[63/100] bg-slate-700 rounded flex items-center justify-center text-slate-600 text-xs">{side}</div>;
                            })()}
                            <div>
                                <p className="text-white text-sm font-semibold capitalize">{side === 'front' ? 'Fronte' : 'Retro'}</p>
                                <p className="text-slate-500 text-xs">{template[side].elements.length} elementi</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {template[side].elements.map(el => (
                                        <span key={el.id} className="text-[10px] bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded">{el.type}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BadgeBatchPage;
